import { useCallback, useState } from 'react';
import { Box, Flex, IconButton, Image, Link, Text, Tooltip } from '@chakra-ui/react';
import { EditIcon } from '@chakra-ui/icons';
import type { ImageBlock as ImageBlockType } from '../../editor/types';
import { ADMIN_COLORS } from '../../../../../../generated/tokens/constants';
import { usePreviewDevice } from '../PreviewDeviceContext';
import { useEditorBlockContext } from '../EditorBlockContext';
import { AssetPickerModal } from '../../editor/components/AssetPickerModal';
import { CropIcon } from '../../assets/components/CropIcon';

type ImageBlockProps = ImageBlockType & { blockId?: string };

function getImageUrl(data: Record<string, unknown>, preferredResolution?: string): string | null {
  // If a resolution preference is set, try that first
  if (preferredResolution && typeof data[preferredResolution] === 'string') {
    return data[preferredResolution] as string;
  }
  // CDN data object — try common keys (descending quality)
  if (typeof data.url === 'string') return data.url;
  if (typeof data.src === 'string') return data.src;
  if (typeof data.original === 'string') return data.original;
  if (typeof data.large === 'string') return data.large;
  if (typeof data.medium === 'string') return data.medium;
  return null;
}

export function ImageBlock({ props, resolved_data, blockId }: ImageBlockProps): React.ReactElement {
  const { alt, aspect_ratio, link_url, mobile_image_id, display_mode, width, height, object_fit, resolution } = props;
  const previewDevice = usePreviewDevice();
  const editorCtx = useEditorBlockContext();
  const isInEditor = editorCtx?.isEditorCanvas === true && blockId !== undefined;
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const isInline = display_mode === 'inline';
  const effectiveWidth = width ?? '100%';
  const effectiveHeight = height ?? (aspect_ratio ? undefined : 'auto');
  const effectiveObjectFit = object_fit ?? 'cover';

  const handleImageSelect = useCallback(
    (imageId: number) => {
      if (editorCtx?.onBlockPropsChange && blockId) {
        editorCtx.onBlockPropsChange(editorCtx.sectionId, blockId, { image_id: imageId });
      }
    },
    [editorCtx, blockId],
  );

  const handleCrop = useCallback(() => {
    const imgId = props.image_id;
    if (imgId && imgId > 0 && editorCtx?.onCropImage) {
      const resolved = editorCtx.resolvedImageMap[imgId];
      const url = resolved?.data?.url ?? resolved?.data?.original ?? resolved?.data?.large ?? '';
      editorCtx.onCropImage(imgId, url);
    }
  }, [editorCtx, props.image_id]);

  // Error / missing state
  if (!resolved_data || resolved_data.error) {
    return (
      <Box
        gc-testing-id="blockImage"
        bg={ADMIN_COLORS.card}
        border="1px dashed"
        borderColor={ADMIN_COLORS.border}
        display={isInline ? 'inline-flex' : 'flex'}
        alignItems="center"
        justifyContent="center"
        minH="120px"
        borderRadius="md"
        width={isInline ? effectiveWidth : undefined}
        {...(aspect_ratio ? { sx: { aspectRatio: aspect_ratio } } : {})}
        cursor={isInEditor ? 'pointer' : undefined}
        onDoubleClick={
          editorCtx
            ? (e) => {
                e.stopPropagation();
                setIsPickerOpen(true);
              }
            : undefined
        }
      >
        <Text color={ADMIN_COLORS.textMuted} fontSize="sm">
          {resolved_data?.error ?? 'Image not loaded'}
        </Text>
        {isInEditor && (
          <AssetPickerModal
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            onSelect={handleImageSelect}
            currentImageId={props.image_id ?? null}
            defaultImageType="landing-inline"
          />
        )}
      </Box>
    );
  }

  const desktopUrl = getImageUrl(resolved_data.data, resolution);
  const mobileUrl =
    mobile_image_id && resolved_data.mobile_data
      ? getImageUrl(resolved_data.mobile_data, resolution)
      : null;

  const imageProps = {
    width: '100%' as const,
    height: effectiveHeight,
    objectFit: effectiveObjectFit,
    alt: alt,
    ...(aspect_ratio ? { sx: { aspectRatio: aspect_ratio } } : {}),
  };

  const renderImage = (): React.ReactElement => {
    if (mobileUrl && desktopUrl) {
      // When preview device is set, show only the appropriate image
      if (previewDevice === 'mobile') {
        return <Image src={mobileUrl} {...imageProps} />;
      }
      if (previewDevice === 'desktop') {
        return <Image src={desktopUrl} {...imageProps} />;
      }
      // Production: use Chakra responsive display
      return (
        <>
          <Image
            src={mobileUrl}
            display={{ base: 'block', md: 'none' }}
            {...imageProps}
          />
          <Image
            src={desktopUrl}
            display={{ base: 'none', md: 'block' }}
            {...imageProps}
          />
        </>
      );
    }

    return <Image src={desktopUrl ?? undefined} {...imageProps} />;
  };

  const content = renderImage();

  const containerDisplay = isInline ? 'inline-block' : 'block';
  const containerWidth = isInline ? effectiveWidth : undefined;

  // Wrap with editor interactivity when in editor context
  if (isInEditor && !link_url) {
    return (
      <Box
        gc-testing-id="blockImage"
        display={containerDisplay}
        width={containerWidth}
        position="relative"
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        onDoubleClick={(e) => {
          e.stopPropagation();
          setIsPickerOpen(true);
        }}
        cursor="pointer"
      >
        {content}

        {/* Floating action bar */}
        {showActions && (
          <Flex
            gc-testing-id="image-block-actions"
            position="absolute"
            top={2}
            right={2}
            gap={1}
            bg="rgba(0,0,0,0.75)"
            borderRadius="md"
            p={1}
            zIndex={10}
          >
            <Tooltip label="Change Image" placement="bottom" hasArrow>
              <IconButton
                gc-testing-id="image-block-change-btn"
                aria-label="Change Image"
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                color="white"
                _hover={{ bg: 'whiteAlpha.200' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPickerOpen(true);
                }}
              />
            </Tooltip>
            {props.image_id && props.image_id > 0 && (
              <Tooltip label="Crop" placement="bottom" hasArrow>
                <IconButton
                  gc-testing-id="image-block-crop-btn"
                  aria-label="Crop"
                  icon={<CropIcon boxSize="14px" />}
                  size="xs"
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCrop();
                  }}
                />
              </Tooltip>
            )}
          </Flex>
        )}

        <AssetPickerModal
          isOpen={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          onSelect={handleImageSelect}
          currentImageId={props.image_id ?? null}
          defaultImageType="landing-inline"
        />
      </Box>
    );
  }

  if (link_url) {
    return (
      <Link gc-testing-id="blockImage" href={link_url} display={containerDisplay} width={containerWidth}>
        {content}
      </Link>
    );
  }

  return (
    <Box gc-testing-id="blockImage" display={containerDisplay} width={containerWidth}>
      {content}
    </Box>
  );
}
